import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useTranslation } from '../../contexts/TranslationContext';
import { API_CONFIG } from '../../config/api';
import {
  Bars3Icon,
  UserIcon,
  Cog6ToothIcon,
  ArrowsRightLeftIcon,
  CheckIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline';

interface HeaderProps {
  onToggleSidebar: () => void;
}

export function Header({ onToggleSidebar }: HeaderProps) {
  const { user, location, availableLocations, selectLocation, logout } = useAuth();
  const permissions = usePermissions();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLocationDropdownOpen, setIsLocationDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const locationDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (locationDropdownRef.current && !locationDropdownRef.current.contains(event.target as Node)) {
        setIsLocationDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get full profile picture URL
  const getProfilePictureUrl = (url: string | null | undefined): string | undefined => {
    if (!url) return undefined;
    if (url.startsWith('http')) return url;
    return `${API_CONFIG.BASE_URL}${url}`;
  };

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleLocationDropdownToggle = () => {
    setIsLocationDropdownOpen(!isLocationDropdownOpen);
  };

  const handleLocationChange = async (locationId: string) => {
    try {
      await selectLocation(locationId);
      setIsLocationDropdownOpen(false);
    } catch (error) {
      console.error('Failed to change location:', error);
    }
  };

  const handleMenuItemClick = (action: string) => {
    setIsDropdownOpen(false);
    
    switch (action) {
      case 'profile':
        navigate('/profile');
        break;
      case 'settings':
        navigate('/settings');
        break;
      case 'billing':
        navigate('/billing');
        break;
      case 'company-settings':
        navigate('/company-settings');
        break;
      case 'organization':
        // TODO: Navigate to organization page when implemented
        console.log('Navigate to organization page');
        break;
      default:
        console.log(`Clicked: ${action}`);
    }
  };

  return (
    <header className="bg-white/80 backdrop-blur-lg shadow-lg border-b border-gray-200/50 sticky top-0 z-30 md:sticky fixed md:relative w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Company/Location Info */}
          <div className="flex items-center animate-fade-in space-x-3">
            <button
              onClick={onToggleSidebar}
              className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white shadow-lg ring-1 ring-gray-200 text-gray-600 hover:text-gray-900 hover:shadow-xl transition-all duration-200 lg:hidden"
              aria-label="Open main menu"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
            {location && (
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-2">
                  <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-lg font-bold text-gray-900 truncate max-w-[200px] sm:max-w-none">
                    {location.name}
                  </span>
                </div>
                
                {/* Location Changer - only show if user has multiple locations */}
                {availableLocations.length > 1 && (
                  <div className="relative" ref={locationDropdownRef}>
                    <button
                      onClick={handleLocationDropdownToggle}
                      className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                      aria-label="Change location"
                      title="Change location"
                    >
                      <ArrowsRightLeftIcon className="h-5 w-5 text-gray-500" />
                    </button>

                    {/* Location Dropdown */}
                    {isLocationDropdownOpen && (
                      <div className="absolute left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                        <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-100">
                          Switch Location
                        </div>
                        {availableLocations.map((loc) => (
                          <button
                            key={loc.id}
                            onClick={() => handleLocationChange(loc.id)}
                            className={`w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center justify-between transition-colors ${
                              location.id === loc.id ? 'bg-primary-50' : ''
                            }`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium truncate ${
                                location.id === loc.id ? 'text-primary-700' : 'text-gray-900'
                              }`}>
                                {loc.name}
                              </div>
                              {loc.address && (
                                <div className="text-xs text-gray-500 truncate">
                                  {loc.address}
                                </div>
                              )}
                            </div>
                            {location.id === loc.id && (
                              <CheckIcon className="h-5 w-5 text-primary-600 flex-shrink-0 ml-2" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-6 animate-slide-down">
            {user && (
              <div className="flex items-center space-x-4">
                {/* User name and role - hidden on mobile */}
                <div className="text-right hidden md:block">
                  <div className="text-base font-semibold text-gray-900">
                    {user.first_name} {user.last_name}
                  </div>
                  <div className="text-sm text-gray-500 capitalize flex items-center">
                    <span className="inline-block w-2 h-2 bg-success-500 rounded-full mr-2"></span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      user.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                      user.role === 'manager' ? 'bg-blue-100 text-blue-800' :
                      user.role === 'therapist' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {user.role === 'admin' && '🛡️'}
                      {user.role === 'manager' && '👔'}
                      {user.role === 'therapist' && '👩‍⚕️'}
                      {user.role === 'receptionist' && '👤'}
                      <span className="ml-1">
                        {user.role === 'admin' ? t('roleAdmin') :
                         user.role === 'manager' ? t('roleManager') :
                         user.role === 'therapist' ? t('roleTherapist') :
                         user.role === 'receptionist' ? t('roleReceptionist') :
                         user.role}
                      </span>
                    </span>
                  </div>
                </div>
                {/* User Avatar Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={handleDropdownToggle}
                    className="h-12 w-12 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 overflow-hidden"
                    aria-label="User menu"
                  >
                    {user.profilePictureUrl ? (
                      <img
                        src={getProfilePictureUrl(user.profilePictureUrl)}
                        alt={`${user.first_name} ${user.last_name}`}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center">
                        <span className="text-sm font-bold text-white">
                          {user.first_name[0]}{user.last_name[0]}
                        </span>
                      </div>
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      {/* Profile */}
                      <button
                        onClick={() => handleMenuItemClick('profile')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <UserIcon className="h-4 w-4 mr-2 text-gray-500" />
                        {t('profile')}
                      </button>



                      {/* Settings */}
                      <button
                        onClick={() => handleMenuItemClick('settings')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <Cog6ToothIcon className="h-4 w-4 mr-2 text-gray-500" />
                        {t('settings')}
                      </button>

                      {/* Billing */}
                      <button
                        onClick={() => handleMenuItemClick('billing')}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                      >
                        <CurrencyDollarIcon className="h-4 w-4 mr-2 text-gray-500" />
                        {t('billing')}
                      </button>

                      {/* Divider */}
                      <div className="border-t border-gray-100 my-1"></div>

                      {/* Sign Out */}
                      <button
                        onClick={logout}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center"
                      >
                        <svg className="h-4 w-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        {t('signOut')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
